import React, { useEffect, useState, useCallback } from "react";
import { KeyRound, Plus, Search, Loader2, Eye, EyeOff, Copy, Pencil, Trash2, ExternalLink, Lock, Building2, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

const CATS = ["Général", "Hébergement", "Nom de domaine", "CMS / Back-office", "Réseaux sociaux", "Email", "Paiement", "Analytics", "Autre"];
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function VaultPage() {
  const { selectedId, managed } = useCompany();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [revealed, setRevealed] = useState({});
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const cq = selectedId && selectedId !== "all" ? `?company_id=${selectedId}` : ""; const { data } = await api.get(`/vault${cq}`); setEntries(data || []); }
    catch { setEntries([]); } finally { setLoading(false); }
  }, [selectedId]);
  useEffect(() => { load(); setRevealed({}); }, [load]);

  const reveal = async (e) => {
    if (revealed[e.id]) { setRevealed((r) => { const n = { ...r }; delete n[e.id]; return n; }); return; }
    try {
      const { data } = await api.post(`/vault/${e.id}/reveal`);
      setRevealed((r) => ({ ...r, [e.id]: data.password }));
      setTimeout(() => setRevealed((r) => { const n = { ...r }; delete n[e.id]; return n; }), 30000);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const copyPwd = async (e) => {
    try {
      const pwd = revealed[e.id] ?? (await api.post(`/vault/${e.id}/reveal`)).data.password;
      await navigator.clipboard.writeText(pwd || "");
      toast.success("Mot de passe copié");
    } catch { toast.error("Copie impossible"); }
  };

  const save = async () => {
    if (!editing.label?.trim()) { toast.error("Un libellé est requis"); return; }
    if (!editing.company_id) { toast.error("Choisissez une entreprise"); return; }
    setSaving(true);
    try {
      if (editing.id) { await api.patch(`/vault/${editing.id}`, editing); toast.success("Accès mis à jour"); }
      else { await api.post("/vault", editing); toast.success("Accès ajouté"); }
      setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Supprimer l'accès « ${e.label} » ?`)) return;
    try { await api.delete(`/vault/${e.id}`); toast.success("Accès supprimé"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const defaultCompany = selectedId && selectedId !== "all" ? selectedId : (managed[0]?.id || "");
  const openNew = () => setEditing({ company_id: defaultCompany, label: "", category: "Général", username: "", password: "", url: "", hint: "", notes: "", client_email: "" });

  const filtered = entries.filter((e) => !q || [e.label, e.username, e.url, e.category, e.company_name].some((x) => (x || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="h-full flex flex-col" data-testid="vault-page">
      <div className="h-14 px-4 flex items-center gap-3 border-b border-white/10 shrink-0">
        <ShieldCheck size={18} className="text-klein" />
        <h1 className="font-cabinet font-bold text-lg">Coffre Accès</h1>
        <span className="text-xs text-zinc-500 tabular-nums">{entries.length}</span>
        <div className="flex-1" />
        <div className="hidden sm:flex items-center gap-2 bg-background/50 border border-white/10 rounded-lg px-3 h-9 w-64">
          <Search size={14} className="text-zinc-500" />
          <input data-testid="vault-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="flex-1 bg-transparent text-sm focus:outline-none" />
        </div>
        <button data-testid="vault-new" onClick={openNew} className="flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-klein text-white text-sm font-semibold hover:brightness-110 transition"><Plus size={16} /> Accès</button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4 text-xs text-zinc-400 bg-klein/5 border border-klein/15 rounded-xl px-3.5 py-2.5 max-w-2xl">
          <Lock size={14} className="text-klein shrink-0" /> Les mots de passe sont chiffrés (AES/Fernet) et masqués par défaut. La révélation est journalisée et se re-masque après 30 secondes.
        </div>
        {loading ? <div className="flex justify-center py-16 text-zinc-400"><Loader2 className="animate-spin" /></div>
          : filtered.length === 0 ? <div className="text-center py-16 text-zinc-500 text-sm">{q ? "Aucun résultat." : "Aucun accès enregistré. Ajoutez les identifiants de vos clients en toute sécurité."}</div>
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {filtered.map((e) => (
                <div key={e.id} data-testid={`vault-${e.id}`} className="rounded-2xl border border-white/10 bg-card p-4 hover:border-klein/30 transition-colors group">
                  <div className="flex items-start justify-between gap-2 mb-2.5">
                    <div className="min-w-0">
                      <div className="font-cabinet font-bold text-sm truncate">{e.label}</div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-klein/15 text-klein font-medium">{e.category}</span>
                        {e.company_name && <span className="text-[10px] text-zinc-500 flex items-center gap-0.5 truncate"><Building2 size={9} />{e.company_name}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <button data-testid={`vault-edit-${e.id}`} onClick={() => setEditing({ ...e, password: "" })} className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/[0.08]"><Pencil size={14} /></button>
                      <button data-testid={`vault-delete-${e.id}`} onClick={() => remove(e)} className="p-1.5 rounded-lg text-zinc-400 hover:text-signal hover:bg-signal/10"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  {e.username && <div className="flex items-center gap-2 text-xs text-zinc-300 mb-1.5"><span className="text-zinc-500 w-16 shrink-0">Identifiant</span><span className="truncate font-mono-label">{e.username}</span></div>}
                  <div className="flex items-center gap-2 text-xs mb-1.5">
                    <span className="text-zinc-500 w-16 shrink-0">Mot de passe</span>
                    <span className="flex-1 font-mono-label truncate text-zinc-200">{e.has_password ? (revealed[e.id] ?? "••••••••••") : <span className="text-zinc-600">aucun</span>}</span>
                    {e.has_password && <>
                      <button data-testid={`vault-reveal-${e.id}`} onClick={() => reveal(e)} title={revealed[e.id] ? "Masquer" : "Révéler"} className="p-1 rounded text-zinc-400 hover:text-klein">{revealed[e.id] ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                      <button onClick={() => copyPwd(e)} title="Copier" className="p-1 rounded text-zinc-400 hover:text-klein"><Copy size={13} /></button>
                    </>}
                  </div>
                  {e.url && <a href={e.url.startsWith("http") ? e.url : `https://${e.url}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-klein hover:underline mb-1.5 truncate"><ExternalLink size={11} className="shrink-0" /> {e.url}</a>}
                  {e.hint && <div className="text-[11px] text-zinc-400 italic mt-1">💡 {e.hint}</div>}
                  <div className="flex items-center gap-1 text-[10px] text-zinc-500 mt-2.5 pt-2 border-t border-white/[0.06]"><Clock size={9} /> MAJ {fmtDate(e.password_updated_at || e.updated_at)}{e.updated_by_name ? ` · ${e.updated_by_name}` : ""}</div>
                </div>
              ))}
            </div>
          )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><KeyRound size={18} className="text-klein" /> {editing?.id ? "Modifier l'accès" : "Nouvel accès"}</DialogTitle><DialogDescription className="sr-only">Identifiants chiffrés</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              <Field label="Entreprise">
                <select data-testid="vault-company" value={editing.company_id} onChange={(e) => setEditing({ ...editing, company_id: e.target.value })} disabled={!!editing.id} className="w-full bg-background border border-white/10 rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 disabled:opacity-60">
                  {managed.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Libellé *"><input data-testid="vault-label" value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} placeholder="ex : WordPress du site MRG" className="fd-input" /></Field>
              <Field label="Catégorie">
                <select data-testid="vault-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full bg-background border border-white/10 rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20">
                  {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Identifiant"><input data-testid="vault-username" value={editing.username} onChange={(e) => setEditing({ ...editing, username: e.target.value })} placeholder="login / email" className="fd-input" /></Field>
              <Field label={editing.id ? "Mot de passe (laisser vide = inchangé)" : "Mot de passe"}><input data-testid="vault-password" type="text" value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} placeholder="••••••••" className="fd-input font-mono-label" /></Field>
              <Field label="Lien (back-office)"><input data-testid="vault-url" value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} placeholder="https://…/wp-admin" className="fd-input" /></Field>
              <Field label="Indice (sans révéler le mot de passe)"><input data-testid="vault-hint" value={editing.hint} onChange={(e) => setEditing({ ...editing, hint: e.target.value })} placeholder="ex : finit par l'année de création" className="fd-input" /></Field>
              <Field label="Notes"><textarea value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} rows={2} className="fd-input resize-none" /></Field>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="h-9 px-4 rounded-lg border border-white/10 text-sm">Annuler</button>
            <button data-testid="vault-save" onClick={save} disabled={saving} className="h-9 px-4 rounded-lg bg-klein text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-1.5">{saving && <Loader2 size={14} className="animate-spin" />} {editing?.id ? "Enregistrer" : "Ajouter"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-[11px] font-medium text-zinc-400 mb-1 block">{label}</label>{children}</div>;
}
